import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';

import {
  MdwSwapAnimateComponent,
  MdwSwapTemplateDirective,
} from '@spaces-ui/animations/angular-css';
import { MdwIcon } from '@spaces-ui/meadow/icons';
import { DropdownArrow } from '@spaces-ui/meadow/icons/svgs';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'button.mdw-button , a.mdw-button',
  imports: [CommonModule, MdwIcon, MdwSwapAnimateComponent, MdwSwapTemplateDirective],
  templateUrl: './button.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './button.scss',
})
export class MdwButton implements OnInit, AfterViewInit, OnDestroy {
  public element: ElementRef<HTMLButtonElement> = inject(ElementRef<HTMLButtonElement>);
  public textContent = viewChild<ElementRef<HTMLDivElement>>('textContent');

  public type = input<'button' | 'submit' | 'reset'>('button');
  public badge = input<string | number>();
  public badgeColor = input<'Critical' | 'Warning'>('Critical');
  public passiveBadge = input<boolean>();
  public icon = input<string>();
  public color = input<string>();
  public iconPresetColorNumber = input<number>();
  public contentIsIcon = input(false);
  public iconCalloutColor = input<string>();
  public iconCalloutSize = input<'medium' | 'large' | 'x-large'>();
  public fillTextContent = input<boolean>();

  public showDownArrow = input<boolean>(false);
  public iconSize = signal<string>('medium');

  public colorClass = signal<string>('');

  public icons = { DropdownArrow };

  private mutationObserver?: MutationObserver;
  private _hasText = signal<boolean>(false);

  @HostBinding('class.has-icon')
  public get hasIcon(): boolean {
    return !!this.icon();
  }

  @HostBinding('class.content-is-icon')
  public get contentIsIconClass(): boolean {
    return this.contentIsIcon();
  }

  @HostBinding('class.has-text')
  public get hasText(): boolean {
    return this._hasText();
  }

  public classes = computed(() => {
    const iconSize = this.iconSize();
    const colorClass = this.colorClass();
    const iconPresetColorNumber = this.iconPresetColorNumber();
    const iconCalloutColor = this.iconCalloutColor();
    const iconCalloutSize = this.iconCalloutSize();

    const classes = {
      [`${iconSize}-icon`]: true,
      [colorClass]: true,
      [`color-preset-${iconPresetColorNumber}`]: iconPresetColorNumber !== undefined,
      [`icon-callout-${iconCalloutColor ?? 'neutral'}`]: iconCalloutColor !== undefined,
      [`${iconCalloutSize ?? 'medium'}-callout`]: iconCalloutSize !== undefined,
      'icon-callout': iconCalloutColor !== undefined,
    };

    return classes;
  });

  public button: HTMLButtonElement = this.element.nativeElement;

  public ngOnInit(): void {
    if (this.button.classList.contains('x-small-button')) {
      this.iconSize.set('x-small');
    } else if (this.button.classList.contains('small-button')) {
      this.iconSize.set('small');
    }

    if (this.color()) {
      this.colorClass.set(`color-${this.color()}`);
    }

    this.button.type = this.type();
  }

  public ngAfterViewInit(): void {
    const textContentRef = this.textContent();

    if (textContentRef) {
      this._updateHasText(textContentRef.nativeElement);

      this.mutationObserver = new MutationObserver(() => {
        this._updateHasText(textContentRef.nativeElement);
      });

      this.mutationObserver.observe(textContentRef.nativeElement, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  public ngOnDestroy(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  private _updateHasText(element: HTMLElement): void {
    const text = element.textContent ?? '';
    const onlyHasOneChild = element.children.length === 1;
    const childIsAvatar = element.children[0]?.tagName === 'MDW-AVATAR';
    const childIsIcon = element.children[0]?.tagName === 'MDW-ICON';
    if (onlyHasOneChild && (childIsAvatar || childIsIcon)) {
      this._hasText.set(false);
      return;
    }

    this._hasText.set(text.trim().length > 0);
  }
}
